import { PRIVILEGES, type AuthUser, type PrivilegeId } from '@stayon/auth-core';
import { can } from '@/shared/auth';

export type ObjectAreaId =
  | 'overview'
  | 'stammdaten'
  | 'anfahrt'
  | 'parken'
  | 'ressourcen'
  | 'hausordnung'
  | 'nachrichten'
  | 'gastportal'
  | 'veroeffentlichung';

export type ObjectAreaGroupId = 'objekt' | 'gastinfos' | 'kommunikation' | 'vertrieb';

export type ObjectAreaPath =
  | '/property-groups/$id'
  | '/property-groups/$id/stammdaten'
  | '/property-groups/$id/anfahrt'
  | '/property-groups/$id/parken'
  | '/property-groups/$id/ressourcen'
  | '/property-groups/$id/hausordnung'
  | '/property-groups/$id/nachrichten'
  | '/property-groups/$id/gastportal'
  | '/property-groups/$id/veroeffentlichung';

export interface ObjectArea {
  id: ObjectAreaId;
  label: string;
  group: ObjectAreaGroupId;
  to: ObjectAreaPath;
  /** Spiegelt den Route-Guard. Ohne Angabe genügt das Objekt-Leserecht der Elternroute. */
  access?: 'manager' | PrivilegeId;
}

export const OBJECT_AREA_GROUPS: { id: ObjectAreaGroupId; label: string }[] = [
  { id: 'objekt', label: 'Objekt' },
  { id: 'gastinfos', label: 'Gastinfos' },
  { id: 'kommunikation', label: 'Kommunikation' },
  { id: 'vertrieb', label: 'Vertrieb' },
];

export const OBJECT_AREAS: ObjectArea[] = [
  { id: 'overview', label: 'Übersicht', group: 'objekt', to: '/property-groups/$id' },
  {
    id: 'stammdaten',
    label: 'Stammdaten',
    group: 'objekt',
    to: '/property-groups/$id/stammdaten',
  },
  {
    id: 'ressourcen',
    label: 'Ressourcen',
    group: 'objekt',
    to: '/property-groups/$id/ressourcen',
    access: 'manager',
  },
  {
    id: 'anfahrt',
    label: 'Anfahrt',
    group: 'gastinfos',
    to: '/property-groups/$id/anfahrt',
    access: 'manager',
  },
  {
    id: 'parken',
    label: 'Parken',
    group: 'gastinfos',
    to: '/property-groups/$id/parken',
    access: 'manager',
  },
  {
    id: 'hausordnung',
    label: 'Hausordnung',
    group: 'gastinfos',
    to: '/property-groups/$id/hausordnung',
  },
  {
    id: 'nachrichten',
    label: 'Nachrichten',
    group: 'kommunikation',
    to: '/property-groups/$id/nachrichten',
    access: PRIVILEGES.guestExperienceView,
  },
  {
    id: 'gastportal',
    label: 'Gastportal',
    group: 'kommunikation',
    to: '/property-groups/$id/gastportal',
    access: PRIVILEGES.guestExperienceView,
  },
  {
    id: 'veroeffentlichung',
    label: 'Veröffentlichung',
    group: 'vertrieb',
    to: '/property-groups/$id/veroeffentlichung',
    access: 'manager',
  },
];

/** Ein Bereich ohne Recht erscheint gar nicht — nicht abgeblendet (FRONTEND_GUIDELINES §10). */
export function visibleObjectAreas(user: AuthUser | null, canManageObject: boolean): ObjectArea[] {
  return OBJECT_AREAS.filter((area) => {
    if (!area.access) return true;
    return area.access === 'manager' ? canManageObject : can(user, area.access);
  });
}

/**
 * Welcher Bereich zum Pfad gehört. Die Übersicht ist das Präfix aller anderen und zählt deshalb
 * nur bei exakter Übereinstimmung.
 */
export function activeObjectArea(
  areas: ObjectArea[],
  groupId: string,
  pathname: string,
): ObjectArea | undefined {
  return areas.find((area) => {
    const path = area.to.replace('$id', groupId);
    if (pathname === path || pathname === `${path}/`) return true;
    return area.id !== 'overview' && pathname.startsWith(`${path}/`);
  });
}
